import { Calendar, Clock, Edit3, KeyRound, Mail, MapPin, Phone, User } from "lucide-react";
import { useState } from "react";
import AppButton from "../../../components/ui/AppButton";
import { useAuth } from "../../../hooks/useAuth";
import EditProfileModal from "../components/EditProfileModal";
import UpdatePasswordModal from "../components/UpdatePasswordModal";
import SettingsCard from "../ui/SettingsCard";
import {
  formatDateTime,
  getInitials,
  getRoleLabel,
  resolveImageUrl,
} from "../ui/settingsUtils";

const ProfileContent = () => {
  const { user } = useAuth();
  const [openModal, setOpenModal] = useState<"password" | "edit" | null>(null);

  const imageUrl = resolveImageUrl(user?.imageUrl);
  const initials = getInitials(user?.fullName);
  const roleLabel = getRoleLabel(user?.roles, user?.userType);

  const details = [
    { label: "Full name", value: user?.fullName, icon: User },
    { label: "Email address", value: user?.email, icon: Mail },
    { label: "Phone number", value: user?.phoneNumber, icon: Phone },
    { label: "Home address", value: user?.address, icon: MapPin },
    { label: "Member since", value: formatDateTime(user?.createdOn), icon: Calendar },
    { label: "Last login", value: formatDateTime(user?.lastLogin), icon: Clock },
  ];

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-[#EEEFEA] bg-white shadow-[0_1px_2px_rgba(28,28,28,0.04)]">
        <div className="h-28 bg-[linear-gradient(135deg,#0E7A99_0%,#0E7A99_46%,#929E69_100%)]" />
        <div className="px-6 pb-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={user?.fullName || "Profile"}
                  className="-mt-10 h-24 w-24 rounded-2xl border-4 border-white object-cover shadow-md"
                />
              ) : (
                <div className="-mt-10 flex h-24 w-24 items-center justify-center rounded-2xl border-4 border-white bg-[#EDF2DF] text-3xl font-semibold text-primary shadow-md">
                  {initials}
                </div>
              )}
              <div className="pb-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-[22px] font-semibold text-hColor">
                    {user?.fullName || "Your profile"}
                  </h2>
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    {roleLabel}
                  </span>
                </div>
                <p className="mt-1 text-sm text-pColor">{user?.email}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pb-1">
              <AppButton
                type="button"
                appearance="soft"
                variant="secondary"
                onClick={() => setOpenModal("password")}
                icon={<KeyRound size={15} />}
                className="h-[40px] rounded-lg px-4"
              >
                Change password
              </AppButton>
              <AppButton
                type="button"
                onClick={() => setOpenModal("edit")}
                icon={<Edit3 size={15} />}
                className="h-[40px] rounded-lg px-4"
              >
                Edit profile
              </AppButton>
            </div>
          </div>
        </div>
      </section>

      <SettingsCard
        title="Personal details"
        description="This is the information other people in your company will see."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {details.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.label}
                className="flex items-start gap-3 rounded-xl bg-[#F8F8F5] px-4 py-3.5"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
                  <Icon size={16} />
                </span>
                <div className="min-w-0">
                  <p className="text-[12px] font-medium text-pColor">
                    {item.label}
                  </p>
                  <p className="mt-0.5 truncate text-sm font-semibold text-hColor">
                    {item.value || "—"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </SettingsCard>

      <UpdatePasswordModal
        isOpen={openModal === "password"}
        onClose={() => setOpenModal(null)}
      />
      <EditProfileModal
        isOpen={openModal === "edit"}
        onClose={() => setOpenModal(null)}
      />
    </div>
  );
};

export default ProfileContent;
